'use client';

import { useRef, useState, useEffect, useCallback } from 'react';

import { CONFIG } from 'src/config-global';

import { shouldPromptForUpdate } from 'src/utils/app-version';

// ----------------------------------------------------------------------

/**
 * Detects that a newer build has been deployed while the app is open.
 *
 * Each deploy writes `version.json` next to `index.html` (see the frontend
 * deploy workflows). That file is served with `max-age=0, must-revalidate`,
 * so polling it always reflects what is live, while the revision compiled
 * into this bundle stays fixed at `CONFIG.site.buildRevision`. A mismatch
 * means the tab is running an older build than the one deployed.
 *
 * Detection only — this hook never reloads; the caller decides what to do
 * with the signal (e.g. hard-reload on the next navigation).
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

type VersionResponse = { revision?: string };

export function useAppVersionCheck(): { updateAvailable: boolean } {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Read inside the effect via ref so a detected update stops further polling
  // without re-creating the interval on every render.
  const updateAvailableRef = useRef(false);
  updateAvailableRef.current = updateAvailable;

  const currentRevision = CONFIG.site.buildRevision;

  const checkVersion = useCallback(async () => {
    if (updateAvailableRef.current) return;

    try {
      const response = await fetch(`${CONFIG.site.basePath}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      const { revision }: VersionResponse = await response.json();

      if (shouldPromptForUpdate(currentRevision, revision)) {
        setUpdateAvailable(true);
      }
    } catch {
      // Offline or a transient CDN error — the next poll retries. Never
      // surface this to the user; a failed check is not an update.
    }
  }, [currentRevision]);

  useEffect(() => {
    // No revision compiled in (local dev, or a build predating this feature):
    // every comparison would be a false positive, so stay disabled.
    if (!currentRevision) return undefined;

    checkVersion();

    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Returning to a backgrounded tab is the most likely moment to be running
    // a stale build, and timers are throttled while hidden — so re-check.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkVersion, currentRevision]);

  return { updateAvailable };
}
